class PopupTriggerManager {
  constructor(element) {
    this.element = element;

    this.sectionId =
      element.dataset.sectionId;

    this.storageKey =
      `popup_${this.sectionId}`;

    this.context = {
      isCustomer:
        element.dataset.customer === 'true'
    };
    this.context.isSubscribedNewsletter = this.context.isCustomer && element.dataset.subscribedNewsletter === 'true';

    this.config = this.loadConfig();

    this.escapeKeyListener = null;
  }

  loadConfig() {
    const configNode =
      document.getElementById(
        `PopupTriggerConfig-${this.sectionId}`
      );

    if (!configNode) {
      return { triggers: [] };
    }

    try {
      return JSON.parse(
        configNode.textContent
      );
    } catch (error) {
      console.error(
        'Popup trigger config is invalid',
        error
      );

      return null;
    }
  }

  async start() {
    if (!this.config) {
      this.closePopup();
      return;
    }

    const canShow =
      await this.evaluateTriggers();

    if (!canShow) {
      this.closePopup();
      return;
    }

    this.openPopup();

    this.registerCloseTracking();
  }

  async evaluateTriggers() {
    const triggers =
      this.config?.triggers || [];

    let isDelayIncluded = !!triggers.find(trigger => trigger.type === 'delay');
    const delayAttribute =
      document.querySelector(`#Popup-${this.sectionId}`)?.getAttribute('data-managed-delay') ||
      document.querySelector(`#Popup-${this.sectionId}`)?.getAttribute('data-delay');
    if (!isDelayIncluded && delayAttribute) {
      triggers.push({
        type: 'delay',
        seconds: Number(delayAttribute || 5)
      });
    }

    if (!this.isFrequencyIncluded()) {
      triggers.push({
        type: 'frequency',
        days: Number(1)
      });
    }

    const results =
      await Promise.all(
        triggers.map((trigger) =>
          this.evaluateTrigger(trigger)
        )
      );

    return results.every(Boolean);
  }

  async evaluateTrigger(trigger) {
    switch (trigger.type) {

      case 'audience': {
        const isAudienceMatched = this.evaluateAudience(
          trigger
        );
        if (!isAudienceMatched) {
          console.warn(
            `Audience trigger not matched for popup "${this.sectionId}"`
          );
        }
        return isAudienceMatched;
      }

      case 'frequency': {
        const isFrequencyMatched = this.evaluateFrequency(
          trigger
        );
        if (!isFrequencyMatched) {
          console.warn(
            `Frequency trigger not matched for popup "${this.sectionId}"`
          );
        }
        return isFrequencyMatched;
      }

      case 'delay':
        return this.evaluateDelay(
          trigger
        );

      case 'page': {
        const isPageMatched = this.evaluatePage(
          trigger
        );
        if (!isPageMatched) {
          console.warn(
            `Page trigger not matched for popup "${this.sectionId}"`
          );
        }
        return isPageMatched;
      }

      default:
        console.warn(
          `Unknown popup trigger "${trigger.type}"`
        );

        return true;
    }
  }

  evaluateAudience(trigger) {
    const audiences =
      trigger.values || [];

    if (!audiences.length) {
      return true;
    }

    return audiences.some(
      (audience) => {
        switch (audience) {

          case 'guest':
            return !this.context.isCustomer;

          case 'customer':
            return this.context.isCustomer;

          case 'customer_not_newsletter':
            return this.context.isCustomer && !this.context.isSubscribedNewsletter;

          case 'all':
            return true;

          default:
            return false;
        }
      }
    );
  }

  isFrequencyIncluded() {
    const triggers =
      this.config?.triggers || [];

    return !!triggers.find(trigger => trigger.type === 'frequency');
  }

  evaluateFrequency(trigger) {
    const days =
      Number(trigger.days || 0);

    if (days <= 0) {
      return true;
    }

    let lastSeen =
      localStorage.getItem(
        this.storageKey
      );

    if (!this.isFrequencyIncluded()) {
      lastSeen = sessionStorage.getItem(
        this.storageKey
      );
    }

    if (!lastSeen) {
      return true;
    }

    const elapsedMs =
      Date.now() - Number(lastSeen);

    const elapsedDays =
      elapsedMs /
      (1000 * 60 * 60 * 24);

    return elapsedDays >= days;
  }

  evaluateDelay(trigger) {
    return new Promise((resolve) => {
      const seconds =
        Number(trigger.seconds || 0);

      setTimeout(
        () => resolve(true),
        seconds * 1000
      );
    });
  }

  evaluatePage(trigger) {
    const pages =
      trigger.values || [];

    if (!pages.length) {
      return true;
    }

    const currentPath = window.location.pathname;
    const rootPath = new URL(
      window.theme?.routes?.root_url || '/',
      window.location.origin
    ).pathname.replace(/\/$/, '');
    const localizedPath = rootPath && currentPath === rootPath
      ? '/'
      : rootPath && currentPath.startsWith(`${rootPath}/`)
        ? currentPath.slice(rootPath.length)
        : currentPath;

    return pages.some(
      (page) => {
        if (page === 'HOMEPAGE' && localizedPath === '/') {
          return true;
        }
        return localizedPath.startsWith(page);
      }
    );
  }

  openPopup() {
    /*
     * Adapt according to your modal-dialog implementation.
     */

    this.markAsShown();

    this.element.removeAttribute(
      'hidden'
    );

    if (
      typeof this.element.show ===
      'function'
    ) {
      this.element.show();
      return;
    }

    this.element.setAttribute(
      'open',
      ''
    );
  }

  closePopup(e) {
    if (e) e.stopImmediatePropagation();
    this.element.setAttribute('hidden', '');

    if (
      typeof this.element.close ===
      'function'
    ) {
      this.element.close();
      return;
    }

    this.element.removeAttribute(
      'open'
    );

    if (this.escapeKeyListener) document.removeEventListener('keydown', this.escapeKeyListener);
  }

  registerCloseTracking() {
    const closeButton =
      this.element.querySelector(
        '.product-popup-modal__toggle'
      );

    if (closeButton) closeButton.addEventListener(
      'click',
      (e) => {
        if (e) e.stopImmediatePropagation();
        this.closePopup(e);
      },
      { once: true }
    );

    this.escapeKeyListener = (e) => {
      if (e.key === 'Escape') {
        this.closePopup(e);
      }
    };
    document.addEventListener('keydown', this.escapeKeyListener);
  }

  markAsShown() {
    if (this.isFrequencyIncluded()) {
      localStorage.setItem(
        this.storageKey,
        Date.now().toString()
      );
    } else {
      sessionStorage.setItem(
        this.storageKey,
        Date.now().toString()
      );
    }

  }
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    document
      .querySelectorAll(
        '[data-trigger-enabled="true"]'
      )
      .forEach((element) => {
        new PopupTriggerManager(
          element
        ).start();
      });
  }
);